import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Upload, X, CheckCircle, Loader, LogOut, Image as ImageIcon, AlertCircle, Key, Copy, RefreshCw, CheckSquare, Square, Search, Eye, Trash2 } from 'lucide-react';
import './AdminUpload.css';

const API_URL = 'https://image-upload-api-gamma.vercel.app/api/images';

const WEDDING_CATEGORIES = [
  { value: 'bride', label: 'Bride' },
  { value: 'groom', label: 'Groom' },
  { value: 'wedding', label: 'Wedding Ceremony' },
  { value: 'family', label: 'Family Photos' },
  { value: 'reception', label: 'Reception' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'pre-wedding', label: 'Pre-Wedding Shoot' },
  { value: 'candid', label: 'Candid Moments' },
  { value: 'group', label: 'Group Photos' },
];

function AdminUpload({ onLogout }) {
  const [weddingName, setWeddingName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [showAllFiles, setShowAllFiles] = useState(false);
  const [weddingPassword, setWeddingPassword] = useState(null);
  const [showPasswordCopied, setShowPasswordCopied] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  // Cover photo upload
  const [deletingCover, setDeletingCover] = useState(null); // 'main' | 'category' | null

  // Image viewer
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewerWedding, setViewerWedding] = useState('');
  const [viewerCategory, setViewerCategory] = useState('');
  const [viewerImages, setViewerImages] = useState([]);
  const [loadingViewer, setLoadingViewer] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [searchNumber, setSearchNumber] = useState('');
  const [selectedViewerImages, setSelectedViewerImages] = useState(new Set());
  const [viewerBulkSelectMode, setViewerBulkSelectMode] = useState(false);

  // Cover photo selection modal
  const [showCoverSelection, setShowCoverSelection] = useState(false);
  const [coverSelectionType, setCoverSelectionType] = useState('');
  const [coverSelectionImages, setCoverSelectionImages] = useState([]);
  const [loadingCoverSelection, setLoadingCoverSelection] = useState(false);
  const [coverSearchQuery, setCoverSearchQuery] = useState('');

  // Calculate current covers
  const currentMainCover = useMemo(() => {
    if (!metadata?.images || !weddingName) return null;
    return metadata.images.find(img =>
      img.category === `weddings/${weddingName.trim()}/landing` && img.isCover
    );
  }, [metadata, weddingName]);

  const currentCategoryCover = useMemo(() => {
    if (!metadata?.images || !weddingName || !selectedCategory) return null;
    return metadata.images.find(img =>
      img.category === `weddings/${weddingName.trim()}/${selectedCategory}` && img.isCover
    );
  }, [metadata, weddingName, selectedCategory]);

  // Derive existing categories from metadata for this wedding
  const existingCategories = useMemo(() => {
    if (!metadata?.images || !weddingName) return [];
    const cats = new Set();
    metadata.images.forEach(img => {
      if (img.category?.includes('/')) {
        const parts = img.category.split('/');
        if (parts[1] === weddingName.trim() && parts[2] && parts[2] !== 'landing') {
          cats.add(parts[2]);
        }
      }
    });
    return Array.from(cats).sort();
  }, [metadata, weddingName]);

  // availableCategories useMemo removed as it's unused (replaced by existingCategories + Add New option logic)


  useEffect(() => {
    const savedWedding = localStorage.getItem('currentWeddingName');
    if (savedWedding) {
      setWeddingName(savedWedding);
    }
  }, []);

  // Redundant useEffect removed


  const [allWeddings, setAllWeddings] = useState([]);

  // ... (existing state)

  useEffect(() => {
    fetchWeddingsList();
  }, []);

  const fetchWeddingsList = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/weddings`);
      setAllWeddings(data.weddings || []);
    } catch (error) {
      console.error('Failed to fetch weddings list:', error);
    }
  };

  useEffect(() => {
    const savedWedding = localStorage.getItem('currentWeddingName');
    if (savedWedding) {
      setWeddingName(savedWedding);
      loadMetadata(savedWedding);
    } else {
      // If no wedding selected, we don't load huge metadata anymore.
      // We just rely on allWeddings for the dropdown.
    }
  }, []);

  useEffect(() => {
    setWeddingPassword(null); // Clear password when name changes to prevent mismatch
    if (weddingName.trim()) {
      localStorage.setItem('currentWeddingName', weddingName.trim());
      // fetchWeddingPassword call removed to prevent auto-generation on typing
      loadMetadata(weddingName.trim());
      setIsAddingNewCategory(false); // Reset when wedding changes
    } else {
      localStorage.removeItem('currentWeddingName');
      setMetadata(null); // Clear metadata if no wedding selected
    }
  }, [weddingName]);

  // Removed general loadMetadata useEffect to avoid double calls

  const loadMetadata = async (targetWeddingName) => {
    if (!targetWeddingName) return;
    try {
      // Fetch ONLY the specific wedding data
      const { data } = await axios.get(`${API_URL}/gallery`, {
        params: { weddingName: targetWeddingName }
      });
      setMetadata(data);
    } catch (error) {
      console.error('Failed to load metadata:', error);
    }
  };

  const fetchWeddingPassword = async (name) => {
    try {
      const response = await axios.post(`${API_URL}/verify-wedding-password`, {
        weddingName: name
      });
      setWeddingPassword(response.data.password);
    } catch (error) {
      console.error('Failed to fetch password:', error);
    }
  };

  const generatePasswordsForExisting = async () => {
    if (!window.confirm('Generate passwords for all existing weddings that don\'t have one?')) {
      return;
    }

    setMigrating(true);
    try {
      const response = await axios.post(`${API_URL}/generate-passwords-for-existing`);
      const { newPasswordsGenerated, newPasswords } = response.data;

      if (newPasswordsGenerated > 0) {
        let message = `✅ Generated ${newPasswordsGenerated} new password(s):\n\n`;
        Object.entries(newPasswords).forEach(([wedding, pass]) => {
          message += `${wedding}: ${pass}\n`;
        });
        alert(message);
      } else {
        alert('All existing weddings already have passwords!');
      }

      if (weddingName.trim()) {
        fetchWeddingPassword(weddingName.trim());
      }
    } catch (error) {
      console.error('Migration failed:', error);
      alert('Failed to generate passwords. Please try again.');
    } finally {
      setMigrating(false);
    }
  };

  const copyPasswordToClipboard = () => {
    if (weddingPassword) {
      navigator.clipboard.writeText(weddingPassword);
      setShowPasswordCopied(true);
      setTimeout(() => setShowPasswordCopied(false), 2000);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
    setSelectedFiles(new Set());
    setBulkSelectMode(false);
  };

  // Unused handleCoverPhotoSelect removed


  // ─── Cover Photo CRUD ───

  const uploadCoverToR2AndSave = async (file, categoryPath, weddingNameValue) => {
    const fileName = categoryPath.endsWith('/landing')
      ? `landing-cover-${Date.now()}.jpg`
      : `cover-${selectedCategory}-${Date.now()}.jpg`;

    // 1. Get presigned URL
    const { data } = await axios.post(`${API_URL}/upload`, {
      fileName,
      fileType: file.type,
      category: categoryPath,
    });

    // 2. Upload to R2
    await axios.put(data.uploadUrl, file, {
      headers: { 'Content-Type': file.type },
    });

    // 3. Save to cover_photos collection
    await axios.post(`${API_URL}/cover-photos`, {
      image: {
        name: fileName,
        url: data.publicUrl,
        key: data.key,
      },
      categoryPath,
      weddingName: weddingNameValue,
    });

    return { fileName, publicUrl: data.publicUrl };
  };

  // Unused uploadCoverPhoto removed (logic moved to CoverPhotoPreview or handled inline)


  const deleteCoverPhoto = async (type) => {
    const categoryPath = type === 'main'
      ? `weddings/${weddingName.trim()}/landing`
      : `weddings/${weddingName.trim()}/${selectedCategory}`;

    const label = type === 'main' ? 'Main Landing Page Cover' : `${selectedCategory} Category Cover`;

    if (!window.confirm(`Delete the ${label}? This cannot be undone.`)) {
      return;
    }

    setDeletingCover(type);
    try {
      await axios.delete(`${API_URL}/cover-photos`, {
        data: {
          weddingName: weddingName.trim(),
          categoryPath,
        },
      });

      alert(`✅ ${label} deleted successfully!`);
      loadMetadata();
    } catch (error) {
      console.error('Delete cover failed:', error);
      alert('Failed to delete cover photo.');
    } finally {
      setDeletingCover(null);
    }
  };

  // ─── File Management ───

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  };

  const toggleFileSelection = (index) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const toggleBulkSelect = () => {
    setBulkSelectMode(!bulkSelectMode);
    setSelectedFiles(new Set());
  };

  const selectAllFiles = () => {
    if (selectedFiles.size === files.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(files.map((_, i) => i)));
    }
  };

  const removeSelectedFiles = () => {
    if (selectedFiles.size === 0) return;
    if (window.confirm(`Remove ${selectedFiles.size} selected file(s)?`)) {
      setFiles(prev => prev.filter((_, i) => !selectedFiles.has(i)));
      setSelectedFiles(new Set());
      setBulkSelectMode(false);
    }
  };

  const clearAllFiles = () => {
    if (uploading) return;
    if (window.confirm(`Remove all ${files.length} selected files?`)) {
      setFiles([]);
      setProgress({});
      setSelectedFiles(new Set());
      setBulkSelectMode(false);
    }
  };

  const clearWeddingName = () => {
    if (uploading) return;
    if (files.length > 0) {
      if (!window.confirm('Clearing wedding name will remove all selected files. Continue?')) {
        return;
      }
    }
    setWeddingName('');
    setSelectedCategory('');
    setFiles([]);
    setProgress({});
    setWeddingPassword(null);
    setSelectedFiles(new Set());
    setBulkSelectMode(false);
    localStorage.removeItem('currentWeddingName');
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuthenticated');
    localStorage.removeItem('currentWeddingName');
    onLogout();
  };

  // ─── Upload Images ───

  const uploadImages = async () => {
    if (!weddingName.trim()) {
      alert('Please enter the wedding name');
      return;
    }
    if (!selectedCategory) {
      alert('Please select a category');
      return;
    }
    if (files.length === 0) {
      alert('Please select at least one image');
      return;
    }
    if (files.length > 100) {
      const confirmed = window.confirm(
        `You're about to upload ${files.length} images. This may take several minutes. Continue?`
      );
      if (!confirmed) return;
    }

    setUploading(true);
    const uploadedImages = [];
    let failedCount = 0;

    const BATCH_SIZE = 10;
    const totalBatches = Math.ceil(files.length / BATCH_SIZE);

    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      const start = batchIndex * BATCH_SIZE;
      const end = Math.min(start + BATCH_SIZE, files.length);
      const batch = files.slice(start, end);

      const batchResults = await Promise.all(
        batch.map(async (file, indexInBatch) => {
          const globalIndex = start + indexInBatch;
          try {
            setProgress(prev => ({ ...prev, [globalIndex]: 'uploading' }));
            const categoryPath = `weddings/${weddingName.trim()}/${selectedCategory}`;

            const { data } = await axios.post(`${API_URL}/upload`, {
              fileName: file.name,
              fileType: file.type,
              category: categoryPath,
            });

            await axios.put(data.uploadUrl, file, {
              headers: { 'Content-Type': file.type },
            });

            setProgress(prev => ({ ...prev, [globalIndex]: 'completed' }));

            return {
              success: true,
              image: {
                name: file.name,
                url: data.publicUrl,
                key: data.key,
                weddingName: weddingName.trim(),
                category: selectedCategory,
                displayNumber: globalIndex + 1,
              }
            };
          } catch (error) {
            console.error(`Failed to upload ${file.name}:`, error);
            setProgress(prev => ({ ...prev, [globalIndex]: 'error' }));
            return { success: false };
          }
        })
      );

      for (const result of batchResults) {
        if (result.success) {
          uploadedImages.push(result.image);
        } else {
          failedCount++;
        }
      }
    }

    const METADATA_CHUNK_SIZE = 50;
    try {
      const categoryLabelObj = WEDDING_CATEGORIES.find(cat => cat.value === selectedCategory);
      const categoryLabel = categoryLabelObj ? categoryLabelObj.label : selectedCategory;

      for (let i = 0; i < uploadedImages.length; i += METADATA_CHUNK_SIZE) {
        const chunk = uploadedImages.slice(i, i + METADATA_CHUNK_SIZE);
        await axios.post(`${API_URL}/metadata`, {
          images: chunk,
          category: `weddings/${weddingName.trim()}/${selectedCategory}`,
          categoryLabel,
        });
      }
    } catch (error) {
      console.error('Failed to save some metadata:', error);
    }

    setUploading(false);
    loadMetadata();

    const successCount = uploadedImages.length;
    if (successCount === files.length) {
      alert(`✅ Successfully uploaded all ${successCount} images!\n\nWedding: ${weddingName}\nCategory: ${selectedCategory}\nPassword: ${weddingPassword}\n\nShare the password with the couple to access their gallery.`);
      setFiles([]);
      setProgress({});
      setSelectedCategory('');
      setSelectedFiles(new Set());
      setBulkSelectMode(false);
    } else {
      alert(
        `Upload Complete!\n✅ Successful: ${successCount}\n❌ Failed: ${failedCount}\n\nWedding Password: ${weddingPassword}\n\nFailed images are marked in red.`
      );
    }
  };

  const retryFailedUploads = () => {
    const failedIndices = Object.entries(progress)
      .filter(([_, status]) => status === 'error')
      .map(([index]) => parseInt(index));

    const failedFiles = failedIndices.map(i => files[i]);
    const newProgress = { ...progress };
    failedIndices.forEach(i => delete newProgress[i]);
    setProgress(newProgress);
    setFiles(failedFiles);
    setSelectedFiles(new Set());
    setBulkSelectMode(false);
  };

  // ─── Image Viewer ───

  const openImageViewer = () => {
    setShowImageViewer(true);
    setViewerWedding('');
    setViewerCategory('');
    setViewerImages([]);
    setSearchNumber('');
    setSelectedViewerImages(new Set());
    setViewerBulkSelectMode(false);
  };

  const loadViewerImages = async () => {
    if (!viewerWedding.trim() || !viewerCategory) {
      alert('Please select wedding and category');
      return;
    }

    setLoadingViewer(true);
    setLoadingViewer(true);
    try {
      // Use the new gallery API to fetch images for the specific wedding
      const { data } = await axios.get(`${API_URL}/gallery`, {
        params: { weddingName: viewerWedding.trim() }
      });

      const images = data.images?.filter((img) => {
        if (img.category?.includes('/') && !img.isCover) {
          const parts = img.category.split('/');
          // We still need to filter by category if a specific one is selected in the viewer
          // The API returns ALL images for the wedding.

          if (viewerCategory === 'all') {
            return parts[2] !== 'landing';
          }
          return parts[2] === viewerCategory;
        }
        return false;
      }) || [];

      images.sort((a, b) => {
        const dateA = new Date(a.uploadedAt || 0);
        const dateB = new Date(b.uploadedAt || 0);
        return dateA - dateB;
      });

      setViewerImages(images);
    } catch (error) {
      console.error('Failed to load images:', error);
      alert('Failed to load images');
    } finally {
      setLoadingViewer(false);
    }
  };

  const scrollToImage = () => {
    if (!searchNumber) return;
    const imageNumber = parseInt(searchNumber);
    if (imageNumber < 1 || imageNumber > viewerImages.length) {
      alert(`Please enter a number between 1 and ${viewerImages.length}`);
      return;
    }

    const element = document.getElementById(`viewer-image-${imageNumber - 1}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('highlight-flash');
      setTimeout(() => element.classList.remove('highlight-flash'), 2000);
    }
  };

  const toggleViewerImageSelection = (index) => {
    setSelectedViewerImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const toggleViewerBulkSelect = () => {
    setViewerBulkSelectMode(!viewerBulkSelectMode);
    setSelectedViewerImages(new Set());
  };

  const selectAllViewerImages = () => {
    if (selectedViewerImages.size === viewerImages.length) {
      setSelectedViewerImages(new Set());
    } else {
      setSelectedViewerImages(new Set(viewerImages.map((_, i) => i)));
    }
  };

  const copySelectedLinks = () => {
    if (selectedViewerImages.size === 0) return;
    const links = Array.from(selectedViewerImages)
      .map(index => viewerImages[index].url)
      .join('\n');
    navigator.clipboard.writeText(links);
    alert(`Copied ${selectedViewerImages.size} links to clipboard!`);
    setSelectedViewerImages(new Set());
    setViewerBulkSelectMode(false);
  };

  // ─── Cover Selection Modal ───

  const openCoverSelection = async (type) => {
    setCoverSelectionType(type);
    setShowCoverSelection(true);
    setLoadingCoverSelection(true);
    setCoverSearchQuery('');

    try {
      // Use the new gallery API to fetch images for the specific wedding
      const { data } = await axios.get(`${API_URL}/gallery`, {
        params: { weddingName: weddingName.trim() }
      });

      const images = data.images?.filter((img) => {
        if (img.category?.includes('/') && !img.isCover) {
          const parts = img.category.split('/');
          // No need to check wedding name again as API filtered it, but checking !landing is good
          return parts[2] !== 'landing';
        }
        return false;
      }) || [];

      images.sort((a, b) => {
        const dateA = new Date(a.uploadedAt || 0);
        const dateB = new Date(b.uploadedAt || 0);
        return dateA - dateB;
      });

      setCoverSelectionImages(images);
    } catch (error) {
      console.error('Failed to load images:', error);
      alert('Failed to load images for cover selection');
    } finally {
      setLoadingCoverSelection(false);
    }
  };

  const selectCoverPhoto = async (image) => {
    const categoryPath = coverSelectionType === 'main'
      ? `weddings/${weddingName.trim()}/landing`
      : `weddings/${weddingName.trim()}/${selectedCategory}`;

    const label = coverSelectionType === 'main' ? 'Main Landing Page' : selectedCategory;
    const confirmed = window.confirm(`Set "${image.name}" as cover photo for ${label}?`);
    if (!confirmed) return;

    try {
      await axios.post(`${API_URL}/cover-photos`, {
        image: {
          name: image.name,
          url: image.url,
          key: image.key,
        },
        categoryPath,
        weddingName: weddingName.trim(),
      });

      alert(`✅ Cover photo set successfully!`);
      setShowCoverSelection(false);
      loadMetadata();
    } catch (error) {
      console.error('Failed to set cover photo:', error);
      alert('Failed to set cover photo. Please try again.');
    }
  };

  // ─── Stats ───

  const stats = useMemo(() => {
    const completed = Object.values(progress).filter(p => p === 'completed').length;
    const failed = Object.values(progress).filter(p => p === 'error').length;
    const uploadingCount = Object.values(progress).filter(p => p === 'uploading').length;
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    return { completed, failed, uploading: uploadingCount, totalSize };
  }, [progress, files]);

  const displayedFiles = showAllFiles ? files : files.slice(0, 20);
  const hasMoreFiles = files.length > 20;

  const availableWeddings = useMemo(() => {
    return allWeddings.sort();
  }, [allWeddings]);

  // ─── Cover Photo Preview Component ───
  const CoverPhotoPreview = ({ cover, type, onDelete, onReplace, onSelectFromUploads }) => {
    if (!cover) {
      return (
        <div className="cover-photo-upload">
          <input
            type="file"
            id={`${type}-cover-input`}
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              const categoryPath = type === 'main'
                ? `weddings/${weddingName.trim()}/landing`
                : `weddings/${weddingName.trim()}/${selectedCategory}`;

              const confirmed = window.confirm(`Upload "${file.name}" as ${type === 'main' ? 'Main Landing Page' : selectedCategory} cover?`);
              if (!confirmed) return;

              try {
                await uploadCoverToR2AndSave(file, categoryPath, weddingName.trim());
                alert(`✅ Cover photo uploaded!`);
                loadMetadata();
              } catch (err) {
                console.error('Upload failed:', err);
                alert('Failed to upload cover photo.');
              }
            }}
            style={{ display: 'none' }}
          />
          <label htmlFor={`${type}-cover-input`} className="cover-photo-label" style={{ minWidth: 'auto', width: 'auto', display: 'inline-flex' }}>
            <Upload size={18} />
            <span>Upload Cover Photo</span>
          </label>
          <button
            onClick={() => onSelectFromUploads()}
            className="select-from-uploads-btn"
            type="button"
          >
            <ImageIcon size={18} />
            Select from Uploads
          </button>
        </div>
      );
    }

    return (
      <div>
        <div className="current-cover-preview" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <img
            src={`${cover.url}?t=${Date.now()}`}
            alt={`${type} Cover`}
            style={{ width: '120px', height: '75px', objectFit: 'cover', borderRadius: '4px', border: '1px solid var(--border-color)' }}
          />
          <div style={{ flex: 1, minWidth: '150px' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--accent-green)', marginBottom: '0.25rem' }}>
              <CheckCircle size={14} style={{ display: 'inline', marginRight: '4px' }} />
              Cover Set
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              {cover.name}
            </div>
          </div>
        </div>

        <div className="cover-photo-upload" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <input
            type="file"
            id={`${type}-cover-replace`}
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              const categoryPath = type === 'main'
                ? `weddings/${weddingName.trim()}/landing`
                : `weddings/${weddingName.trim()}/${selectedCategory}`;

              const confirmed = window.confirm(`Replace cover with "${file.name}"?`);
              if (!confirmed) return;

              try {
                await uploadCoverToR2AndSave(file, categoryPath, weddingName.trim());
                alert(`✅ Cover photo replaced!`);
                loadMetadata();
              } catch (err) {
                console.error('Replace failed:', err);
                alert('Failed to replace cover photo.');
              }
            }}
            style={{ display: 'none' }}
          />
          <label htmlFor={`${type}-cover-replace`} className="cover-photo-label" style={{ minWidth: 'auto', width: 'auto', display: 'inline-flex' }}>
            <Upload size={16} />
            <span>Replace</span>
          </label>
          <button
            onClick={() => onSelectFromUploads()}
            className="select-from-uploads-btn"
            type="button"
          >
            <ImageIcon size={16} />
            Select from Uploads
          </button>
          <button
            onClick={() => onDelete(type)}
            className="delete-cover-btn"
            disabled={deletingCover === type}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: '0.5rem 1rem',
              background: 'rgba(239, 68, 68, 0.15)',
              color: '#ef4444',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            {deletingCover === type ? (
              <Loader className="spin" size={16} />
            ) : (
              <Trash2 size={16} />
            )}
            <span>Delete</span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-upload-container">
      <div className="admin-header">
        <div className="admin-title">
          <ImageIcon size={32} />
          <div>
            <h1>Gallery Photo Manager</h1>
            <p>TagTeam Studios Admin Portal</p>
          </div>
        </div>
        <div className="header-actions">
          <button onClick={openImageViewer} className="viewer-btn">
            <Eye size={18} />
            View Images
          </button>
          <button onClick={generatePasswordsForExisting} className="migrate-btn" disabled={migrating}>
            <RefreshCw size={18} className={migrating ? 'spin' : ''} />
            {migrating ? 'Generating...' : 'Generate Passwords'}
          </button>
          <button onClick={handleLogout} className="logout-btn">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      <div className="upload-container">
        <div className="form-section">
          <div className="form-row">
            <div className="form-group wedding-name-group">
              <label>Select Existing Gallery</label>
              <select
                value={availableWeddings.includes(weddingName) ? weddingName : ''}
                onChange={(e) => { if (e.target.value) setWeddingName(e.target.value); }}
                className="wedding-select-dropdown"
                style={{ marginBottom: '1rem', padding: '0.875rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px' }}
              >
                <option value="">-- Helper: Select an Existing Gallery --</option>
                {availableWeddings.map(w => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </select>

              <label>Gallery Name *</label>
              <div className="wedding-name-input-wrapper">
                <input
                  type="text"
                  value={weddingName}
                  onChange={(e) => setWeddingName(e.target.value)}
                  placeholder="e.g., Rahul & Priya Wedding, Birthday Party, etc."
                  disabled={uploading}
                />
                {weddingName && (
                  <button onClick={clearWeddingName} className="clear-wedding-btn" disabled={uploading} title="Clear wedding name">
                    <X size={18} />
                  </button>
                )}
              </div>
              {weddingName && (
                <button
                  type="button"
                  onClick={() => fetchWeddingPassword(weddingName.trim())}
                  className="generate-password-btn"
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem',
                    width: '100%',
                    justifyContent: 'center'
                  }}
                >
                  <Key size={16} />
                  Generate / View Password
                </button>
              )}
              {weddingName && (
                <span className="wedding-name-hint">
                  Gallery name is saved. You can upload multiple categories.
                </span>
              )}
            </div>

            {/* ─── Main Cover Photo Section ─── */}
            {weddingName && (
              <div className="main-cover-section" style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                <h3 style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1rem', marginTop: 0 }}>
                  🖼️ Main Gallery Cover (Landing Page Background)
                </h3>
                <CoverPhotoPreview
                  cover={currentMainCover}
                  type="main"
                  onDelete={deleteCoverPhoto}
                  onSelectFromUploads={() => openCoverSelection('main')}
                />
              </div>
            )}

            <div className="form-group">
              <label>Category *</label>
              {weddingName && !isAddingNewCategory ? (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      if (e.target.value === '__NEW_CATEGORY__') {
                        setIsAddingNewCategory(true);
                        setSelectedCategory('');
                      } else {
                        setSelectedCategory(e.target.value);
                      }
                    }}
                    disabled={uploading}
                    style={{
                      flex: 1,
                      padding: '0.875rem 1rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: '1rem',
                      borderRadius: '4px'
                    }}
                  >
                    <option value="">Select Category</option>
                    {existingCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="__NEW_CATEGORY__" style={{ fontWeight: 'bold', color: 'var(--accent-green)' }}>
                      + Add New Category
                    </option>
                  </select>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    list="wedding-categories"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    placeholder="Enter New Category Name"
                    disabled={uploading}
                    style={{
                      flex: 1,
                      padding: '0.875rem 1rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: '1rem',
                      borderRadius: '4px'
                    }}
                  />
                  {weddingName && (
                    <button
                      onClick={() => setIsAddingNewCategory(false)}
                      title="Cancel adding new category"
                      style={{
                        padding: '0 1rem',
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={18} />
                    </button>
                  )}
                  <datalist id="wedding-categories">
                    {WEDDING_CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value} />
                    ))}
                  </datalist>
                </div>
              )}
            </div>
          </div>

          {weddingPassword && (
            <div className="wedding-password-display">
              <div className="password-header">
                <Key size={20} />
                <span>Gallery Password for {weddingName}</span>
              </div>
              <div className="password-content">
                <span className="password-value">{weddingPassword}</span>
                <button onClick={copyPasswordToClipboard} className="copy-password-btn" title="Copy password">
                  <Copy size={18} />
                  {showPasswordCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="password-hint">Share this password with the couple to access their gallery</p>

              <div className="gallery-link-section">
                <div className="link-header">
                  <span className="link-label">Gallery Link:</span>
                  <a
                    href={`${window.location.origin}/gallery-detail/${encodeURIComponent(weddingName.trim())}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gallery-direct-link"
                  >
                    {`${window.location.origin}/gallery-detail/${encodeURIComponent(weddingName.trim())}`}
                  </a>
                </div>
                <div className="gallery-action-buttons">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/gallery-detail/${encodeURIComponent(weddingName.trim())}`);
                      alert('Gallery link copied!');
                    }}
                    className="copy-link-btn"
                  >
                    <Copy size={16} />
                    Copy Link
                  </button>
                  <a
                    href={`${window.location.origin}/gallery-detail/${encodeURIComponent(weddingName.trim())}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-gallery-btn"
                  >
                    <Eye size={16} />
                    View Gallery
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* ─── Category Cover Photo Section ─── */}
          {weddingName && selectedCategory && (
            <div className="cover-photo-section" style={{ padding: '1rem', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)', borderRadius: '6px', marginBottom: '1rem' }}>
              <h3 style={{ marginTop: 0 }}>
                📁 Category Cover: {selectedCategory}
              </h3>
              <CoverPhotoPreview
                cover={currentCategoryCover}
                type="category"
                onDelete={deleteCoverPhoto}
                onSelectFromUploads={() => openCoverSelection('category')}
              />
            </div>
          )}

          <div className="upload-area">
            <input
              type="file"
              id="file-input"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            <label htmlFor="file-input" className="upload-label">
              <Upload size={48} />
              <p>Click to select images</p>
              <span>Select multiple wedding photos (JPG, PNG)</span>
              <span className="filename-hint">Original filenames will be preserved</span>
            </label>
          </div>
        </div>

        {files.length > 0 && (
          <>
            <div className="file-summary">
              <div className="summary-stats">
                <div className="stat-item">
                  <span className="stat-label">Total Files</span>
                  <span className="stat-value">{files.length}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Size</span>
                  <span className="stat-value">{(stats.totalSize / 1024 / 1024).toFixed(2)} MB</span>
                </div>
                {stats.completed > 0 && (
                  <div className="stat-item success">
                    <span className="stat-label">Completed</span>
                    <span className="stat-value">{stats.completed}</span>
                  </div>
                )}
                {stats.failed > 0 && (
                  <div className="stat-item error">
                    <span className="stat-label">Failed</span>
                    <span className="stat-value">{stats.failed}</span>
                  </div>
                )}
              </div>
              {!uploading && (
                <div className="file-actions">
                  <button onClick={toggleBulkSelect} className={`bulk-select-btn ${bulkSelectMode ? 'active' : ''}`}>
                    {bulkSelectMode ? <CheckSquare size={18} /> : <Square size={18} />}
                    {bulkSelectMode ? 'Cancel Select' : 'Bulk Select'}
                  </button>
                  {bulkSelectMode && (
                    <>
                      <button onClick={selectAllFiles} className="select-all-btn">
                        {selectedFiles.size === files.length ? 'Deselect All' : 'Select All'}
                      </button>
                      <button onClick={removeSelectedFiles} className="remove-selected-btn" disabled={selectedFiles.size === 0}>
                        Remove Selected ({selectedFiles.size})
                      </button>
                    </>
                  )}
                  <button onClick={clearAllFiles} className="clear-all-btn">Clear All</button>
                </div>
              )}
            </div>

            {uploading && (
              <div className="upload-progress-bar">
                <div className="progress-info">
                  <span>Uploading: {stats.completed} / {files.length}</span>
                  <span>{Math.round((stats.completed / files.length) * 100)}%</span>
                </div>
                <div className="progress-track">
                  <div className="progress-fill" style={{ width: `${(stats.completed / files.length) * 100}%` }} />
                </div>
              </div>
            )}

            <div className="file-list-simple">
              <div className="file-list-header">
                <h3>Selected Files</h3>
                <span className="filename-mapping-hint">Users will see numbers: 1, 2, 3... (original filenames preserved)</span>
              </div>

              <div className="file-items-simple">
                {displayedFiles.map((file, index) => (
                  <div
                    key={index}
                    className={`file-item-simple ${selectedFiles.has(index) ? 'selected' : ''}`}
                    onClick={() => bulkSelectMode && !uploading && toggleFileSelection(index)}
                  >
                    {bulkSelectMode && !uploading && (
                      <div className="file-checkbox">
                        {selectedFiles.has(index) ? <CheckSquare size={20} className="checked" /> : <Square size={20} />}
                      </div>
                    )}
                    <div className="file-number">#{index + 1}</div>
                    <div className="file-item-info">
                      <ImageIcon size={20} className="file-icon" />
                      <div className="file-details-simple">
                        <span className="file-name-simple">{file.name}</span>
                        <span className="file-size-simple">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                      </div>
                    </div>
                    <div className="file-item-status">
                      {progress[index] === 'uploading' && <Loader className="status-icon spin" size={20} />}
                      {progress[index] === 'completed' && <CheckCircle className="status-icon success" size={20} />}
                      {progress[index] === 'error' && <AlertCircle className="status-icon error" size={20} />}
                      {!uploading && !progress[index] && !bulkSelectMode && (
                        <button onClick={() => removeFile(index)} className="remove-file-btn-simple"><X size={18} /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {hasMoreFiles && !showAllFiles && (
                <button onClick={() => setShowAllFiles(true)} className="show-more-btn">Show All {files.length} Files</button>
              )}
              {showAllFiles && hasMoreFiles && (
                <button onClick={() => setShowAllFiles(false)} className="show-more-btn">Show Less</button>
              )}
            </div>
          </>
        )}

        {stats.failed > 0 && !uploading && (
          <button onClick={retryFailedUploads} className="retry-btn">
            <AlertCircle size={20} />
            Retry Failed Uploads ({stats.failed})
          </button>
        )}

        <button
          onClick={uploadImages}
          disabled={uploading || files.length === 0 || !weddingName.trim() || !selectedCategory}
          className="upload-btn"
        >
          {uploading ? (
            <>
              <Loader className="spin" size={20} />
              Uploading {stats.completed} / {files.length}
            </>
          ) : (
            `Upload ${files.length} Image(s)`
          )}
        </button>
      </div>

      {/* Image Viewer Modal */}
      {showImageViewer && (
        <div className="image-viewer-modal">
          <div className="viewer-modal-content">
            <div className="viewer-modal-header">
              <h2>View Uploaded Images</h2>
              <button onClick={() => setShowImageViewer(false)} className="close-viewer-btn"><X size={24} /></button>
            </div>

            <div className="viewer-filters">
              <div className="viewer-filter-group">
                <label>Gallery Name</label>
                <select value={viewerWedding} onChange={(e) => { setViewerWedding(e.target.value); setViewerImages([]); }}>
                  <option value="">Select Gallery</option>
                  {availableWeddings.map(wedding => (
                    <option key={wedding} value={wedding}>{wedding}</option>
                  ))}
                </select>
              </div>

              <div className="viewer-filter-group">
                <label>Category</label>
                <select value={viewerCategory} onChange={(e) => { setViewerCategory(e.target.value); setViewerImages([]); }}>
                  <option value="">Select Category</option>
                  <option value="all">View All Categories</option>
                  {WEDDING_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                  {existingCategories.filter(cat => !WEDDING_CATEGORIES.some(wc => wc.value === cat)).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <button onClick={loadViewerImages} disabled={loadingViewer} className="load-images-btn">
                {loadingViewer ? (<><Loader className="spin" size={18} />Loading...</>) : 'Load Images'}
              </button>
            </div>

            {viewerImages.length > 0 && (
              <>
                <div className="viewer-search">
                  <input type="number" value={searchNumber} onChange={(e) => setSearchNumber(e.target.value)} placeholder="Enter image number" min="1" max={viewerImages.length} />
                  <button onClick={scrollToImage} className="search-btn"><Search size={18} />Find Image</button>
                  <span className="total-count">Total: {viewerImages.length} images</span>
                </div>

                <div className="viewer-actions">
                  <button onClick={toggleViewerBulkSelect} className={`bulk-select-btn ${viewerBulkSelectMode ? 'active' : ''}`}>
                    {viewerBulkSelectMode ? <CheckSquare size={18} /> : <Square size={18} />}
                    {viewerBulkSelectMode ? 'Cancel Selection' : 'Select Images'}
                  </button>
                  {viewerBulkSelectMode && (
                    <>
                      <button onClick={selectAllViewerImages} className="select-all-btn">
                        {selectedViewerImages.size === viewerImages.length ? 'Deselect All' : 'Select All'}
                      </button>
                      <button onClick={copySelectedLinks} disabled={selectedViewerImages.size === 0} className="copy-selected-btn">
                        <Copy size={18} />Copy Links ({selectedViewerImages.size})
                      </button>
                    </>
                  )}
                </div>

                <div className="viewer-images-list">
                  {viewerImages.map((img, index) => (
                    <div
                      key={index}
                      id={`viewer-image-${index}`}
                      className={`viewer-image-item ${selectedViewerImages.has(index) ? 'selected' : ''}`}
                      onClick={() => viewerBulkSelectMode && toggleViewerImageSelection(index)}
                    >
                      {viewerBulkSelectMode && (
                        <div className="viewer-checkbox">
                          {selectedViewerImages.has(index) ? <CheckSquare size={24} className="checked" /> : <Square size={24} />}
                        </div>
                      )}
                      <div className="viewer-image-number">#{index + 1}</div>
                      <div className="viewer-image-info">
                        <img src={img.url} alt={img.name} loading="lazy" />
                        <div className="viewer-image-details">
                          <strong>Filename:</strong> {img.name}<br />
                          <strong>Uploaded:</strong> {new Date(img.uploadedAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {viewerImages.length === 0 && viewerWedding && viewerCategory && !loadingViewer && (
              <div className="viewer-empty"><p>No images found for this wedding and category</p></div>
            )}
          </div>
        </div>
      )}

      {/* Cover Photo Selection Modal */}
      {showCoverSelection && (
        <div className="image-viewer-modal">
          <div className="viewer-modal-content">
            <div className="viewer-modal-header">
              <h2>Select Cover Photo from All Uploads</h2>
              <button onClick={() => setShowCoverSelection(false)} className="close-viewer-btn"><X size={24} /></button>
            </div>

            {loadingCoverSelection ? (
              <div className="viewer-loading"><Loader className="spin" size={48} /><p>Loading images...</p></div>
            ) : coverSelectionImages.length > 0 ? (
              <>
                <div className="viewer-search" style={{ marginBottom: '1rem' }}>
                  <input
                    type="text"
                    value={coverSearchQuery}
                    onChange={(e) => setCoverSearchQuery(e.target.value)}
                    placeholder="Search by filename..."
                    style={{ flex: 1 }}
                  />
                  <span className="total-count">
                    {coverSelectionImages.filter(img => img.name.toLowerCase().includes(coverSearchQuery.toLowerCase())).length} of {coverSelectionImages.length} images
                  </span>
                </div>

                <div className="cover-selection-grid">
                  {coverSelectionImages
                    .filter(img => img.name.toLowerCase().includes(coverSearchQuery.toLowerCase()))
                    .map((img, index) => {
                      const originalIndex = coverSelectionImages.indexOf(img);
                      const categoryParts = img.category?.split('/') || [];
                      const categoryName = categoryParts[2] || 'unknown';

                      return (
                        <div key={originalIndex} className="cover-selection-item" onClick={() => selectCoverPhoto(img)}>
                          <img src={img.url} alt={img.name} loading="lazy" />
                          <div className="cover-selection-overlay">
                            <CheckCircle size={32} />
                            <span>Select as Cover</span>
                          </div>
                          <div className="cover-selection-info">
                            <div className="cover-selection-number">#{originalIndex + 1}</div>
                            <div className="cover-selection-category" style={{ fontSize: '0.7rem', background: 'rgba(0,0,0,0.6)', padding: '2px 6px', borderRadius: '3px', marginTop: '4px' }}>
                              {categoryName}
                            </div>
                          </div>
                          <div className="cover-selection-filename" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.8)', padding: '4px 8px', fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {img.name}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </>
            ) : (
              <div className="viewer-empty">
                <p>No uploaded images found for this wedding</p>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  Please upload images first before selecting a cover photo
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUpload;